"use client";

import React, { useState, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Avatar } from "primereact/avatar";
import { Toast } from "primereact/toast";
import axios from "axios";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useParams } from "react-router-dom";

import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";

const GroupChat = () => {
  const { groupId } = useParams();
  const [messages, setMessages] = useState([]);
  const [currentUserId, setCurrentUserId] = useState("170235214267");
  const [text, setText] = useState("");
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [linkInput, setLinkInput] = useState("");
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const imageRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const endRef = useRef(null);
  const toast = useRef(null);
  const [groupHeaderInfo, setGroupHeaderInfo] = useState({ name: "Group Chat", platform: "" });

  const { baseUrl, groupMediaBasePath } = useBaseUrl();

  // ✅ axios instance
  const api = axios.create({
    baseURL: `${baseUrl}/`,
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" },
  });

  // 🔥 FETCH MESSAGES API
  const fetchMessages = async () => {
    try {
      const formData = new FormData();
      formData.append("action", "fetchGroupMessages");
      formData.append("group_id", groupId);
      formData.append("limit", "50");

      const res = await api.post("/group.php", formData);
      console.log("Fetched Messages Response:", res.data);
      setMessages(res.data.messages || []);

      if (res.data.owner_id) {
        setCurrentUserId(res.data.owner_id);
      }

      if (res.data.group_name) {
        setGroupHeaderInfo({
          name: res.data.group_name,
          platform: res.data.platform?.toLowerCase() || ""
        });
      }
    } catch (err) {
      console.error("API Error:", err);
    }
  };

  // 🔥 FETCH PENDING MESSAGES API
  const fetchPendingMessages = async () => {
    setLoadingPending(true);
    try {
      const formData = new FormData();
      formData.append("action", "fetchPendingMessages");
      formData.append("group_id", groupId);

      const res = await api.post("/group.php", formData);
      console.log("Fetched Pending Messages Response:", res.data);
      setPendingMessages(res.data.messages || []);
    } catch (err) {
      console.error("Pending API Error:", err);
    }
    setLoadingPending(false);
  };

  useEffect(() => {
    // Reset states when changing groups
    setMessages([]);
    setPendingMessages([]);
    setText("");
    setSelectedFiles([]);

    fetchMessages();
    fetchPendingMessages();
  }, [groupId]);

  // 🔥 APPROVE MESSAGE API
  const approveMessage = async (messageId) => {
    try {
      const formData = new FormData();
      formData.append("action", "approveMessage");
      formData.append("id", messageId);

      const res = await api.post("/group.php", formData);

      if (res.data.error === 0) {
        toast.current.show({
          severity: "success",
          summary: "Approved",
          detail: "Message approved successfully",
        });
        setPendingMessages((prev) => prev.filter((m) => m.id !== messageId));
        fetchMessages();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: res.data.message || "Failed to approve message",
        });
      }
    } catch (err) {
      console.error("Approve API Error:", err);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Server error during approval",
      });
    }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessageToAPI = async (messageText, mediaFiles) => {
    try {
      const formData = new FormData();
      formData.append("action", "sendGroupMessage");
      formData.append("user_id", currentUserId);
      formData.append("group_id", groupId);
      formData.append("message_text", messageText);

      if (mediaFiles && mediaFiles.length > 0) {
        mediaFiles.forEach((media) => {
          if (media.file) {
            formData.append("media_files[]", media.file);
          }
        });
      }

      const res = await api.post("/group.php", formData);
      return res.data;
    } catch (err) {
      console.error("Send Message API Error:", err);
      return null;
    }
  };

  const sendMessage = async () => {
    if (!text.trim() && selectedFiles.length === 0) return;

    const mediaFiles = selectedFiles.map((f) => ({
      type: f.type,
      url: f.url,
      file: f.file,
    }));

    const result = await sendMessageToAPI(text, mediaFiles);

    if (result) {
      const msg = {
        id: Date.now().toString(),
        sender_id: currentUserId,
        first_name: "You",
        profile_img: "",
        message_text: text,
        media_files: selectedFiles.map((f) => ({ type: f.type, url: f.url })),
        sent_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, msg]);
    }

    setText("");
    setSelectedFiles([]);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const newMedia = files.map((file) => ({
      type: "image",
      url: URL.createObjectURL(file),
      file: file,
    }));
    setSelectedFiles((prev) => [...prev, ...newMedia]);
    setShowAttachmentMenu(false);
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const newMedia = files.map((file) => ({
      type: "video",
      url: URL.createObjectURL(file),
      file: file,
    }));
    setSelectedFiles((prev) => [...prev, ...newMedia]);
    setShowAttachmentMenu(false);
  };

  const handleAudioUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const newMedia = files.map((file) => ({
      type: "audio",
      url: URL.createObjectURL(file),
      file: file,
    }));
    setSelectedFiles((prev) => [...prev, ...newMedia]);
    setShowAttachmentMenu(false);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLinkSubmit = async () => {
    if (!linkInput.trim()) return;
    const result = await sendMessageToAPI(linkInput, []);
    if (result) {
      const msg = {
        id: Date.now().toString(),
        sender_id: currentUserId,
        first_name: "You",
        profile_img: "",
        message_text: linkInput,
        media_files: [],
        link: linkInput,
        sent_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, msg]);
    }
    setLinkInput("");
    setShowLinkDialog(false);
  };

  const renderMedia = (mediaItem, isSidebar = false) => {
    if (!mediaItem || !mediaItem.url) return null;
    const isBlobUrl = mediaItem.url.startsWith("blob:");
    const mediaUrl = isBlobUrl ? mediaItem.url : mediaItem.url;
    const mediaType = mediaItem.type?.toLowerCase() || "";

    if (mediaType === "image" || mediaType.includes("image")) {
      return (
        <img
          key={mediaUrl}
          src={mediaUrl}
          alt="Chat image"
          className={`rounded-xl mt-2 ${isSidebar ? "max-w-[120px]" : "max-w-[240px]"} shadow-md object-contain`}
        />
      );
    }
    if (mediaType === "video" || mediaType.includes("video")) {
      return (
        <video key={mediaUrl} controls className={`rounded-xl mt-2 ${isSidebar ? "max-w-[120px]" : "max-w-[240px]"} shadow-md`}>
          <source src={mediaUrl} type="video/mp4" />
        </video>
      );
    }
    if (mediaType === "audio" || mediaType.includes("audio")) {
      return (
        <audio key={mediaUrl} controls className={`mt-2 w-full ${isSidebar ? "max-w-[120px]" : "max-w-[240px]"}`}>
          <source src={mediaUrl} />
        </audio>
      );
    }
    return <div className="text-red-500 text-xs">Unsupported media</div>;
  };

  const isSidebarVisible = groupId === "1" || groupId === "2";

  return (
    <div className="grid grid-cols-12 h-[calc(100vh-72px)] bg-gray-100 overflow-hidden">
      <Toast ref={toast} />

      {/* 🔷 LEFT SIDE (CHAT) */}
      <div className={`${isSidebarVisible ? "col-span-9" : "col-span-12"} flex flex-col h-full border-r bg-white min-h-0`}>
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white shadow-sm">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar icon="pi pi-users" shape="circle" className="bg-blue-100 text-blue-600" size="large" />
              {groupHeaderInfo.platform && (
                <img
                  src={groupHeaderInfo.platform.includes("youtube") ? "https://cdn-icons-png.flaticon.com/512/1384/1384060.png" : "https://cdn-icons-png.flaticon.com/512/1384/1384053.png"}
                  alt="platform"
                  className="w-5 h-5 absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm"
                />
              )}
            </div>
            <div>
              <h2 className="font-semibold text-lg text-gray-800">{groupHeaderInfo.name}</h2>
              <p className="text-xs text-green-500 font-medium flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Active now
              </p>
            </div>
          </div>
          <div className="flex gap-3 text-gray-500">
            <i className="pi pi-search cursor-pointer hover:text-blue-500"></i>
            <i className="pi pi-ellipsis-v cursor-pointer hover:text-blue-500"></i>
          </div>
        </div>

        {/* CHAT BODY */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50">
          {messages.map((msg) => {
            const isMe = msg.sender_id === currentUserId;
            const mediaFiles = msg.media_files || msg.media || [];

            return (
              <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                <div className={`flex gap-2 items-end max-w-[70%] ${isMe ? "flex-row-reverse" : ""}`}>
                  {!isMe && <Avatar image={msg.profile_img || ""} shape="circle" />}
                  <div className={`px-4 py-2 rounded-2xl shadow-sm ${isMe ? "bg-blue-500 text-white rounded-br-none" : "bg-white text-gray-800 border rounded-bl-none"}`}>
                    {!isMe && <div className="text-xs font-semibold text-blue-600 mb-1">{msg.first_name || "User"}</div>}
                    {msg.message_text && <div className="text-sm">{msg.message_text}</div>}
                    {Array.isArray(mediaFiles) && mediaFiles.length > 0 && mediaFiles.map((m, i) => <div key={i}>{renderMedia(m)}</div>)}
                    {msg.link_id && String(msg.link_id).toLowerCase() !== "null" && (
                      <div className="mt-2 text-center">
                        <Button
                          icon="pi pi-external-link"
                          label="View Link Post"
                          className={`p-button-sm ${isMe ? "bg-white text-blue-600 border-none" : "p-button-outlined"} text-[10px] py-1.5 w-full`}
                          onClick={() => window.open(`/customer/link/details/${msg.link_id}`, '_blank')}
                        />
                      </div>
                    )}
                    <div className="text-[10px] mt-1 text-right opacity-70">
                      {new Date(msg.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef}></div>
        </div>

        {/* INPUT FOOTER */}
        <div className="p-4 bg-white border-t">
          <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-2 border border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
            <div className="relative">
              <Button className="p-button-text text-gray-500 hover:text-blue-500 rounded-full w-10 h-10 flex items-center justify-center" onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}>
                <i className="fas fa-plus-circle text-xl"></i>
              </Button>
              {showAttachmentMenu && (
                <div className="absolute bottom-12 left-0 bg-white shadow-xl rounded-2xl p-3 w-56 z-50 border">
                  <button onClick={() => { imageRef.current.click(); setShowAttachmentMenu(false); }} className="w-full flex items-center gap-3 px-3 py-3 hover:bg-green-50 rounded-xl transition-all">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center"><i className="fas fa-image text-white text-sm"></i></div>
                    <span className="text-sm font-semibold">Image</span>
                  </button>
                  <button onClick={() => { videoRef.current.click(); setShowAttachmentMenu(false); }} className="w-full flex items-center gap-3 px-3 py-3 hover:bg-purple-50 rounded-xl transition-all mt-1">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center"><i className="fas fa-video text-white text-sm"></i></div>
                    <span className="text-sm font-semibold">Video</span>
                  </button>
                  <button onClick={() => { audioRef.current.click(); setShowAttachmentMenu(false); }} className="w-full flex items-center gap-3 px-3 py-3 hover:bg-yellow-50 rounded-xl transition-all mt-1">
                    <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center"><i className="fas fa-music text-white text-sm"></i></div>
                    <span className="text-sm font-semibold">Audio</span>
                  </button>
                </div>
              )}
            </div>
            <input type="file" hidden ref={imageRef} accept="image/*" multiple onChange={handleImageUpload} />
            <input type="file" hidden ref={videoRef} accept="video/*" multiple onChange={handleVideoUpload} />
            <input type="file" hidden ref={audioRef} accept="audio/*" multiple onChange={handleAudioUpload} />

            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a message..."
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), sendMessage())}
              className="flex-1 bg-transparent py-2 px-3 outline-none text-gray-700 text-sm"
            />
            <Button className="p-button-rounded bg-blue-500 border-none w-11 h-11 flex items-center justify-center text-white shadow-lg" onClick={sendMessage} disabled={!text.trim() && selectedFiles.length === 0}>
              <i className="fas fa-paper-plane"></i>
            </Button>
          </div>

          {selectedFiles.length > 0 && (
            <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-2">
              {selectedFiles.map((file, index) => (
                <div key={index} className="relative w-16 h-16 rounded-xl overflow-hidden border-2 border-gray-200">
                  {file.type === "image" ? <img src={file.url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-200 flex items-center justify-center"><i className={`fas fa-${file.type === 'video' ? 'video' : 'music'} text-gray-500`}></i></div>}
                  <button onClick={() => removeSelectedFile(index)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px]"><i className="fas fa-times"></i></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 🔷 RIGHT SIDE (Group Info & Pending) */}
      {isSidebarVisible && (
        <div className="col-span-3 bg-white flex flex-col h-full border-l min-h-0">

          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wider">Pending Approvals</h4>
                <span className="bg-blue-100 text-blue-600 text-xs font-bold px-2 py-1 rounded-full">{pendingMessages.length}</span>
              </div>
              {loadingPending ? (
                <div className="text-center py-4"><i className="pi pi-spin pi-spinner text-blue-500 text-xl"></i></div>
              ) : pendingMessages.length > 0 ? (
                <div className="space-y-3">
                  {pendingMessages.map((msg) => (
                    <div key={msg.id} className="p-3 border rounded-xl bg-gray-50 flex flex-col gap-2 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Avatar image={msg.profile_img} label={!msg.profile_img ? msg.first_name?.charAt(0) : ""} shape="circle" size="small" className="bg-blue-200 text-blue-700" />

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{msg.sender_full_name}</p>
                          <p className="text-[10px] text-gray-500">{new Date(msg.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600">
                        {msg.message_text && <div className="mb-2">{msg.message_text}</div>}
                        {Array.isArray(msg.media_files) && msg.media_files.length > 0 && (
                          <div className="flex flex-wrap gap-2">{msg.media_files.map((m, i) => <div key={i}>{renderMedia(m, true)}</div>)}</div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 mt-2">
                        {msg.link_id && String(msg.link_id).toLowerCase() !== "null" && (
                          <Button icon="pi pi-external-link" label="View Link Post" className="p-button-sm p-button-outlined bg-white text-blue-600 border-blue-600 text-[10px] py-1" onClick={() => window.open(`/customer/link/details/${msg.link_id}`, '_blank')} />
                        )}
                        <Button icon="pi pi-check" label="Approve" className="p-button-sm flex-1 bg-green-500 border-none text-white text-xs py-1.5 px-2" onClick={() => approveMessage(msg.id)} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 border-2 border-dashed rounded-xl border-gray-100"><p className="text-xs text-gray-400">All messages caught up!</p></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupChat;
